import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import moment from 'moment';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Map from '/@/components/map';
import NewOverlayListMobile from '/@/components/map/new-overlay-list-mobile';
import OverlayList from '/@/components/map/overlay-list';
import { useMyFields } from '/@/hooks/fields';
import { useAllFormatedMaps } from '/@/hooks/maps';
import { _ } from '/@/locale';
import { getSelectedFeature } from '/@/store/nav/selectors';
import { getAppInstance, getEnableSpodoptera, getSessionCountry } from '/@/store/session/selectors';
import { trackEvent as trackEventAction } from '/@/store/tracking/actions';
import { isMobile } from '/@/utils';
import { convertCountryBoundsToGoogleLatLng } from '../fields/field-new-v2/utils';
import styles from './styles.module.css';

const Maps = ({ overlayType, overlayVersion, location, tab }) => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const country = useSelector(getSessionCountry);
    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,geometry,externalProvider',
    });
    const appInstance = useSelector(getAppInstance);
    const enableSpodoptera = useSelector((state) => getEnableSpodoptera(state, appInstance));
    const offline = useSelector((state) => state.network.offline);
    const sessionCountry = useSelector(getSessionCountry);
    const [selectedOverlay, setSelectedOverlay] = useState(undefined);
    const selectedFeature = useSelector(getSelectedFeature);
    const trackEvent = bindActionCreators(trackEventAction, dispatch);
    const [activeView, setActiveView] = useState('map');
    const [mapFocused, setMapFocused] = useState(false);
    const [overlays, setOverlays] = useState([]);
    const mapRef = useRef(null);
    const { data: maps, isValidating: loading } = useAllFormatedMaps({ country: sessionCountry.code });

    useEffect(() => {
        if (maps && !loading) {
            const filteredMaps = maps.filter((map) => {
                if (map.slug === 'spodoptera-forecast') {
                    return enableSpodoptera;
                }
                return true;
            });
            setOverlays(filteredMaps);
            handleViewList();
        }
    }, [maps]);

    useEffect(() => {
        if (!overlayType) setSelectedOverlay(undefined);
        if (!overlays) return;
        const overlay = overlays.find(({ slug }) => slug === overlayType);
        if (!overlay) return;
        let version = null;
        version = overlayVersion
            ? overlay.versions.find(({ versionNumber }) => versionNumber === Number(overlayVersion))
            : overlay.versions[0];
        if (!version) return;
        setSelectedOverlay({ overlay, version });
    }, [overlays, overlayType, overlayVersion, tab]);

    const setMapRef = (ref) => {
        mapRef.current = ref;
        focusMap();
    };

    const focusMap = () => {
        if (!mapRef.current) return;
        const googleBounds = convertCountryBoundsToGoogleLatLng(country.bounds);
        mapRef.current.fitBounds(googleBounds);
    };

    const parseOverlay = () => {
        if (!selectedOverlay) return undefined;

        return [...overlays].find(
            ({ type, slug }) => type === selectedOverlay.overlay.type && slug === selectedOverlay.overlay.slug
        )
            ? selectedOverlay
            : undefined;
    };

    const handleViewList = () => {
        setActiveView('list');
    };

    const handleViewMap = () => {
        setActiveView('map');

        if (selectedFeature.selectedFeature === 'maps' && isMobile() && mapFocused === false) {
            setTimeout(() => {
                focusMap();
            });
            setMapFocused(true);
        }
    };

    const handleOverlayChange = (overlay) => {
        if (!overlay) return;
        let params = {};

        const parsed = queryString.parse(location.search);
        if (!overlay) {
            params = Object.entries(parsed).reduce((qs, [key, value]) => {
                if (!['overlayType', 'overlayVersion'].includes(key)) qs[key] = value;
                return qs;
            }, {});
        } else {
            const { type, version } = overlay;
            params = { ...parsed, overlayType: type, overlayVersion: version, tab: 'overlays' };
        }

        handleViewMap();
        return history(
            { search: queryString.stringify(params) },
            {
                replace: true,
            }
        );
    };

    const handleOverlayDeeplinkClick = ({ overlay, version }) => {
        return () => {
            trackEvent({ key: 'maps.spodoptera_forecast', type: 'overlay' });
            handleOverlayChange({ type: overlay.slug, version: version.versionNumber });
        };
    };

    const renderOverlayDeeplink = () => {
        const type = 'spodoptera_forecast';
        const overlay = overlays.find((overlay) => overlay.type === type);

        const selected = selectedOverlay;

        if (tab === 'overlays' || !overlay || (selected && selected.overlay.type === type)) return null;

        const version = overlay.versions[0];
        const created = moment(version.versionNumber.toString());

        if (created.isBefore(moment().subtract(2, 'weeks'))) return null;

        return (
            <div className={styles.overlayDeeplink} onClick={handleOverlayDeeplinkClick({ overlay, version })}>
                <div>
                    {_('Ver pronóstico de Spodoptera')}
                    <p>{_(version.versionLabel)}</p>
                </div>
                <i className='material-icons'>chevron_right</i>
            </div>
        );
    };

    const onOverlayClose = () => {
        const params = {
            overlayType: undefined,
            overlayVersion: undefined,
        };
        return history(
            { search: queryString.stringify(params) },
            {
                replace: true,
            }
        );
    };

    const parsedSelectedOverlay = parseOverlay();

    return (
        <div className={classNames(styles.root, styles[`show-${activeView}`])}>
            {!isMobile() && (
                <div className={styles.sidebar}>
                    {loading ? (
                        <Grid container flexDirection='column' spacing={1}>
                            {Array.from(new Array(3)).map((_, index) => (
                                <Grid item>
                                    <Skeleton index={index} variant='rectangular' width='100%' height={75} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <OverlayList
                            squashSingle
                            overlays={overlays}
                            selected={selectedOverlay}
                            onOverlayClick={handleOverlayChange}
                            sessionCountry={sessionCountry}
                        />
                    )}
                </div>
            )}

            <div className={styles.content}>
                {enableSpodoptera && renderOverlayDeeplink()}

                <Map
                    ref={setMapRef}
                    offline={offline}
                    overlay={parsedSelectedOverlay}
                    onOverlayChange={handleOverlayChange}
                    noGeocoder
                    simplifyPolygons
                    fields={fieldsQuery.data?.results || []}
                    onOverlayClose={onOverlayClose}
                />

                {isMobile() && (
                    <NewOverlayListMobile
                        squashSingle
                        overlays={overlays}
                        selected={selectedOverlay}
                        onOverlayClick={handleOverlayChange}
                        handleOverlayChange={handleOverlayChange}
                        sessionCountry={sessionCountry}
                        overlay={parsedSelectedOverlay}
                    />
                )}
            </div>
        </div>
    );
};

export default Maps;
